import { pricing } from '@/lib/marketing/content'

type Props = {
  onOpenDemo: () => void
}

export function PricingTeaser({ onOpenDemo }: Props) {
  return (
    <section id="pricing" className="py-16 sm:py-24">
      <div className="max-w-[960px] mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-12">
          <div className="inline-block px-3 py-1 rounded-full bg-[#71ED7F]/10 border border-[#71ED7F]/20 text-[#71ED7F] text-xs font-semibold uppercase tracking-wider mb-4">
            {pricing.badge}
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-medium font-display text-white">
            One plan. Everything included.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
          {/* Left: Pricing card */}
          <div className="relative">
            {/* Glow */}
            <div className="absolute -inset-4 bg-[#71ED7F]/[0.06] rounded-3xl blur-2xl pointer-events-none" />

            <div className="relative bg-[#001716] rounded-2xl border border-[#71ED7F]/20 p-6 sm:p-8 h-full shadow-[0_8px_32px_-8px_rgba(0,0,0,0.4)]">
              {/* Price */}
              <div className="text-center mb-6">
                <div className="mb-2">
                  <span className="text-5xl sm:text-6xl font-medium text-white font-display">{pricing.headline}</span>
                  <span className="text-white/40 text-xl">{pricing.period}</span>
                </div>
                <p className="text-sm text-white/40">{pricing.commitment}</p>
              </div>

              {/* Included features */}
              <div className="space-y-3 mb-8">
                {pricing.included.map((feature, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <svg className="w-4 h-4 text-[#71ED7F] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-white/60">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTAs */}
              <div className="space-y-3">
                <button
                  onClick={onOpenDemo}
                  className="block w-full bg-[#71ED7F] text-[#001716] py-3.5 rounded-full text-sm font-semibold text-center hover:shadow-[0_0_45px_-5px_rgba(113,237,127,0.5)] transition-all"
                >
                  {pricing.cta.primary}
                </button>
                <button
                  onClick={onOpenDemo}
                  className="block w-full border border-[#E4FFE7]/10 text-white/60 py-3 rounded-full text-sm font-medium text-center hover:bg-white/5 hover:border-white/15 transition-all"
                >
                  {pricing.cta.secondary}
                </button>
              </div>
            </div>
          </div>

          {/* Right: What you're replacing */}
          <div className="bg-[#001716] rounded-2xl border border-[#E4FFE7]/[0.08] p-6 sm:p-8 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.4)]">
            <h3 className="text-lg font-semibold text-white mb-6">{pricing.replaces.headline}</h3>

            <div className="space-y-4">
              {pricing.replaces.items.map((item, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-[#E4FFE7]/[0.06] last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <span className="text-sm text-white/70">{item.name}</span>
                  </div>
                  <span className="text-sm text-white/40 line-through">{item.cost}</span>
                </div>
              ))}
            </div>

            {/* Separator */}
            <div className="my-6 border-t border-[#71ED7F]/20" />

            {/* Total comparison */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-white/70">Ordo replaces all of this</span>
              <div className="text-right">
                <div className="text-xs text-white/40 line-through mb-0.5">$100-300+/mo</div>
                <div className="text-lg font-semibold text-[#71ED7F]">$199/mo</div>
              </div>
            </div>

            {/* Value prop */}
            <div className="mt-6 p-4 rounded-xl bg-[#71ED7F]/[0.06] border border-[#71ED7F]/10">
              <p className="text-sm text-white/60 text-center">
                <span className="text-[#71ED7F]">Plus:</span> One login. One support team. One bill.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
